import React, { useState } from 'react';

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'disabled' | 'failed';
  createdAt: string;
  lastTriggeredAt: string | null;
  successRate: number;
  totalDeliveries: number;
  failedDeliveries: number;
  secret: string;
}

interface DeliveryLog {
  id: string;
  webhookId: string;
  event: string;
  status: 'success' | 'failed';
  timestamp: string;
  responseCode: number;
  responseTime: number;
  retryCount: number;
}

export const WebhooksPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [newWebhookName, setNewWebhookName] = useState('');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState<string[]>([]);

  // Mock data
  const webhooks: Webhook[] = [
    {
      id: '1',
      name: 'Slack Notifications',
      url: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXX',
      events: ['campaign.created', 'campaign.paused', 'alert.fired'],
      status: 'active',
      createdAt: '2026-01-15',
      lastTriggeredAt: '2026-01-27 10:15 AM',
      successRate: 98.5,
      totalDeliveries: 1245,
      failedDeliveries: 18,
      secret: 'whsec_xxxxxxxxxxxxxxxxxxx',
    },
    {
      id: '2',
      name: 'Data Warehouse Sync',
      url: 'https://api.mydatawarehouse.com/webhooks/gp-ads',
      events: ['campaign.updated', 'spend.threshold'],
      status: 'active',
      createdAt: '2026-01-10',
      lastTriggeredAt: '2026-01-27 09:45 AM',
      successRate: 100,
      totalDeliveries: 3420,
      failedDeliveries: 0,
      secret: 'whsec_yyyyyyyyyyyyyyyyyyy',
    },
    {
      id: '3',
      name: 'Analytics Dashboard',
      url: 'https://analytics.example.com/webhook/receive',
      events: ['alert.fired', 'opportunity.detected'],
      status: 'failed',
      createdAt: '2026-01-05',
      lastTriggeredAt: '2026-01-26 08:30 PM',
      successRate: 45.2,
      totalDeliveries: 892,
      failedDeliveries: 489,
      secret: 'whsec_zzzzzzzzzzzzzzzzzzz',
    },
  ];

  const deliveryLogs: DeliveryLog[] = [
    { id: '1', webhookId: '1', event: 'alert.fired', status: 'success', timestamp: '2026-01-27 10:15 AM', responseCode: 200, responseTime: 245, retryCount: 0 },
    { id: '2', webhookId: '1', event: 'campaign.paused', status: 'success', timestamp: '2026-01-27 09:30 AM', responseCode: 200, responseTime: 189, retryCount: 0 },
    { id: '3', webhookId: '2', event: 'campaign.updated', status: 'success', timestamp: '2026-01-27 09:45 AM', responseCode: 201, responseTime: 312, retryCount: 0 },
    { id: '4', webhookId: '3', event: 'alert.fired', status: 'failed', timestamp: '2026-01-26 08:30 PM', responseCode: 500, responseTime: 5000, retryCount: 3 },
  ];

  const availableEvents = [
    { id: 'campaign.created', label: 'Campaign Created', description: 'New campaign is created' },
    { id: 'campaign.updated', label: 'Campaign Updated', description: 'Campaign details are modified' },
    { id: 'campaign.paused', label: 'Campaign Paused', description: 'Campaign is paused or stopped' },
    { id: 'campaign.deleted', label: 'Campaign Deleted', description: 'Campaign is deleted' },
    { id: 'alert.fired', label: 'Alert Fired', description: 'System alert is triggered' },
    { id: 'opportunity.detected', label: 'Opportunity Detected', description: 'AI detects optimization opportunity' },
    { id: 'spend.threshold', label: 'Spend Threshold', description: 'Budget threshold is reached' },
    { id: 'roas.threshold', label: 'ROAS Threshold', description: 'ROAS drops below threshold' },
  ];

  const handleCreateWebhook = () => {
    console.log('Creating webhook:', { name: newWebhookName, url: newWebhookUrl, events: newWebhookEvents });
    // TODO: Call API
    setShowCreateModal(false);
    setNewWebhookName('');
    setNewWebhookUrl('');
    setNewWebhookEvents([]);
  };

  const handleTestWebhook = (webhook: Webhook) => {
    console.log('Testing webhook:', webhook.id);
    // TODO: Call API to send test payload
    alert(`Test payload sent to ${webhook.url}`);
  };

  const handleDisableWebhook = (id: string) => {
    console.log('Disabling webhook:', id);
    // TODO: Call API
  };

  const handleDeleteWebhook = (id: string) => {
    if (confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
      console.log('Deleting webhook:', id);
      // TODO: Call API
    }
  };

  const handleToggleEvent = (event: string) => {
    if (newWebhookEvents.includes(event)) {
      setNewWebhookEvents(newWebhookEvents.filter(e => e !== event));
    } else {
      setNewWebhookEvents([...newWebhookEvents, event]);
    }
  };

  const handleViewLogs = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setShowLogsModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'disabled': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Webhooks</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Receive real-time event notifications to your applications
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600"
        >
          + Create Webhook
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Webhooks</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{webhooks.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {webhooks.filter(w => w.status === 'active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Deliveries</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {(webhooks.reduce((sum, w) => sum + w.totalDeliveries, 0) / 1000).toFixed(1)}k
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Avg Success Rate</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {(webhooks.reduce((sum, w) => sum + w.successRate, 0) / webhooks.length).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Webhooks List */}
      <div className="space-y-4">
        {webhooks.map((webhook) => (
          <div
            key={webhook.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{webhook.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(webhook.status)}`}>
                    {webhook.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 break-all">{webhook.url}</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handleTestWebhook(webhook)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Test Webhook"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleViewLogs(webhook)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="View Logs"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
                {webhook.status === 'active' && (
                  <button
                    onClick={() => handleDisableWebhook(webhook.id)}
                    className="px-3 py-2 border border-yellow-300 dark:border-yellow-600 text-yellow-600 dark:text-yellow-400 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                    title="Disable"
                  >
                    Disable
                  </button>
                )}
                <button
                  onClick={() => handleDeleteWebhook(webhook.id)}
                  className="px-3 py-2 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Delete"
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400">Created</p>
                <p className="font-medium text-gray-900 dark:text-white">{webhook.createdAt}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Last Triggered</p>
                <p className="font-medium text-gray-900 dark:text-white">{webhook.lastTriggeredAt || 'Never'}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Success Rate</p>
                <p className="font-medium text-gray-900 dark:text-white">{webhook.successRate}%</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Deliveries</p>
                <p className="font-medium text-gray-900 dark:text-white">{webhook.totalDeliveries.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Failed</p>
                <p className="font-medium text-red-600">{webhook.failedDeliveries}</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Events:</p>
              <div className="flex flex-wrap gap-2">
                {webhook.events.map((event) => (
                  <span
                    key={event}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 rounded text-xs font-medium"
                  >
                    {event}
                  </span>
                ))}
              </div>
            </div>

            {webhook.status === 'failed' && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Multiple delivery failures detected. Check endpoint health.
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Documentation */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Webhook Documentation</h3>
            <p className="text-sm text-blue-800 dark:text-blue-400 mb-3">
              Learn about webhook payload structure, retry logic, and security verification.
            </p>
            <a href="/docs/webhooks" className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
              View Webhook Documentation →
            </a>
          </div>
        </div>
      </div>

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Webhook</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Configure a new webhook endpoint</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Webhook Name
                </label>
                <input
                  type="text"
                  value={newWebhookName}
                  onChange={(e) => setNewWebhookName(e.target.value)}
                  placeholder="e.g., Slack Notifications"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Endpoint URL
                </label>
                <input
                  type="url"
                  value={newWebhookUrl}
                  onChange={(e) => setNewWebhookUrl(e.target.value)}
                  placeholder="https://your-domain.com/webhook"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Events to Subscribe
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableEvents.map((event) => (
                    <label
                      key={event.id}
                      className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={newWebhookEvents.includes(event.id)}
                        onChange={() => handleToggleEvent(event.id)}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{event.label}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{event.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewWebhookName('');
                  setNewWebhookUrl('');
                  setNewWebhookEvents([]);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWebhook}
                disabled={!newWebhookName || !newWebhookUrl || newWebhookEvents.length === 0}
                className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Webhook
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Logs Modal */}
      {showLogsModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delivery Logs</h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedWebhook.name}</p>
                </div>
                <button onClick={() => setShowLogsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Response Code</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Response Time</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Retries</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {deliveryLogs
                      .filter(log => log.webhookId === selectedWebhook.id)
                      .map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{log.timestamp}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs">{log.event}</code>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === 'success' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{log.responseCode}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{log.responseTime}ms</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{log.retryCount}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksPage;
